import { TemplateElement, html, defineElement } from '@webtides/element-js';
import { unsafeHTML } from 'lit-html/directives/unsafe-html.js';
import { svgUse } from '../../webresources-sources/js/util/icon.js';

class SurveyStore {
    name;
    TO_SHOW_SECONDS = 45;
    TO_WAIT_SECONDS = 86400;
    MAX_DECLINES = 2;

    confirmed = false;
    declined = 0;
    timeToShow = 1000 * this.TO_SHOW_SECONDS;
    waitUntil = 0;
    callback;

    /**
     * @param {string} name
     * @param {number} timeToShow in seconds
     * @param {number} timeToWait in seconds
     * @param {number} maxDeclines
     */
    constructor(name, timeToShow, timeToWait, maxDeclines) {
        this.name = name;
        this.TO_SHOW_SECONDS = timeToShow;
        this.TO_WAIT_SECONDS = timeToWait;
        this.MAX_DECLINES = maxDeclines;

        this.timeToShow = 1000 * this.TO_SHOW_SECONDS;

        if (!window.localStorage.getItem(this.#storageName)) {
            window.localStorage.setItem(
                this.#storageName,
                JSON.stringify({
                    confirmed: this.confirmed,
                    declined: this.declined,
                    timeToShow: this.timeToShow,
                    waitUntil: this.waitUntil,
                }),
            );
        } else {
            const surveyPopUp = JSON.parse(window.localStorage.getItem(this.#storageName));
            this.confirmed = surveyPopUp.confirmed;
            this.declined = surveyPopUp.declined;
            this.timeToShow = surveyPopUp.timeToShow;
            this.waitUntil = surveyPopUp.waitUntil;
        }

        if (!this.confirmed && this.declined < this.MAX_DECLINES && this.timeToShow === 0) {
            // must have been shown/open but user did not interact with it at all
            this.timeToShow = 1000 * this.TO_SHOW_SECONDS;
        }

        let startingTime = new Date().getTime();

        if (!this.confirmed && this.declined < this.MAX_DECLINES && this.timeToShow > 0) {
            this.timer = setInterval(() => {
                startingTime += 1000;
                if (startingTime >= this.waitUntil) {
                    this.waitUntil = 0;
                    this.timeToShow -= 1000;
                    this.#save();
                    if (this.timeToShow <= 0) {
                        this.#shouldShow();
                        clearInterval(this.timer);
                    }
                }
            }, 1000);
        }
    }

    onShouldShow(callback) {
        this.callback = callback;
    }

    confirm() {
        this.confirmed = true;
        this.timeToShow = 0;
        this.#save();
    }

    decline() {
        this.declined += 1;
        this.timeToShow = this.TO_SHOW_SECONDS * 1000;
        this.waitUntil = new Date().getTime() + this.TO_WAIT_SECONDS * 1000;
        this.#save();
    }

    get #storageName() {
        return `surveyPopUp-${this.name}`;
    }

    #shouldShow() {
        this.waitUntil = 0;
        this.#save();
        if (this.callback) this.callback();
    }

    #save() {
        window.localStorage.setItem(
            this.#storageName,
            JSON.stringify({
                confirmed: this.confirmed,
                declined: this.declined,
                timeToShow: this.timeToShow,
                waitUntil: this.waitUntil,
            }),
        );
    }
}

/**
 * @property {boolean} isPreview
 * @property {boolean} isShown
 * @property {string} name
 * @property {number} timeToShow
 * @property {number} timeToWait
 * @property {number} maxDeclines
 * @property {string} headline
 * @property {string} text
 * @property {string} link
 * @property {string} linkLabel
 * @property {string} linkTarget
 * @property {string} declineLabel
 * @property {string} imageUrl
 * @property {string} imageAlt
 */
export default class PopUp extends TemplateElement {
    /** @type SurveyStore */
    surveyStore;

    constructor() {
        super({ deferUpdate: true, propertyOptions: { isShown: { reflect: true } } });
    }

    properties() {
        return {
            isPreview: false,
            isShown: false,
            name: '',
            timeToShow: 45,
            timeToWait: 86400,
            maxDeclines: 2,
            headline: '',
            text: '',
            link: '',
            linkLabel: '',
            linkTarget: '',
            declineLabel: '',
            imageUrl: '',
            imageAlt: '',
        };
    }

    connected() {
        if (this.isPreview) {
            this.requestUpdate();
        } else {
            this.surveyStore = new SurveyStore(this.name, this.timeToShow, this.timeToWait, this.maxDeclines);
            this.surveyStore.onShouldShow(() => {
                this.isShown = true;
            });
        }

        if (this.imageUrl) {
            this.setAttribute('has-image', 'true');
        }

        this.setAttribute('role', 'dialog');
        this.setAttribute('aria-modal', 'true');
        // when the dialog updates (appears), screen readers will announce it immediately
        this.setAttribute('aria-live', 'assertive');
        this.setAttribute('aria-labelledby', `pop-up-headline-${this.name}`);
        document.body.setAttribute('aria-haspopup', 'dialog');
    }

    watch() {
        return {
            isShown: (isShown) => {
                if (isShown) {
                    this.documentActiveElement = document.activeElement;
                    window.addEventListener('keydown', this.handleKeyDown.bind(this));
                } else {
                    setTimeout(() => {
                        this.style.display = 'none';
                    }, 500);
                    this.documentActiveElement.focus();
                    window.removeEventListener('keydown', this.handleKeyDown);
                }
            },
        };
    }

    handleKeyDown(e) {
        if (e.key === 'Escape') {
            this.isShown = false;
            this.surveyStore?.decline();
        }
    }

    events() {
        return {
            '[confirm-pop-up]': {
                click: () => {
                    this.isShown = false;
                    this.surveyStore?.confirm();
                },
            },
            '[close-pop-up]': {
                click: () => {
                    this.isShown = false;
                    this.surveyStore?.decline();
                },
            },
        };
    }

    template() {
        return html`
            ${this.imageUrl
                ? html`<img src="${this.imageUrl}" width="190" class="image lazyload" alt="${this.imageAlt}" />`
                : ''}
            <div class="content">
                <h2 id="pop-up-headline-${this.name}" class="headline h5 mb-16">${this.headline}</h2>
                <p class="text mb-16 mb-md-32">${unsafeHTML(this.text)}</p>
                <div class="actions d-flex flex-column flex-md-row align-items-center justify-content-between gap-24">
                    ${this.link
                        ? html`
                              <a confirm-pop-up href="${this.link}" target="${this.linkTarget}" class="btn btn-primary"
                                  >${this.linkLabel}</a
                              >
                          `
                        : html`<div></div>`}
                    <button close-pop-up class="btn btn-secondary" type="button">${this.declineLabel}</button>
                </div>
                <button close-pop-up class="close-button btn btn-icon" aria-hidden="true" aria-label="Close">
                    ${svgUse({ name: 'icon-close', cssClasses: 'svg-icon icon-colored icon-size-24' })}
                </button>
            </div>
        `;
    }
}

defineElement('pop-up', PopUp);
